import {get, isEmpty} from 'lodash'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {convertUnit, round} from '../../../../../_gori/helpers'
import {useStatesGlobal} from '../../../../../_gori/helpers/components/StatesGlobalProvider'
import {
  FEDEX_INTERNATIONAL_SERVICES,
  FEDEX_PARCEL_LIMIT,
  OPTION_PACKAGE_TYPE,
  UNIT_VALUES,
} from '../../core/_const'
import {ParcelItem} from './PackageType'
import {WEIGHT_UNIT, DIMENSION_UNIT} from '../../../../../_gori/constants'
import OrderHelper from '../../core/_helpers'

type UsePackageInfoParams = {
  formik: any
  disabled?: boolean
  hasGetPackage?: boolean
  handleAddNewItem?: (section: string) => void
  handleDeleteItem?: (section: string, key: string) => void
  setCurrentDimensionUnit?: (unit: string) => void
  setCurrentWeightUnit?: (unit: string) => void
  isDisabledAddPackage?: boolean
}

export function usePackageInfo({
  formik,
  disabled,
  hasGetPackage = true,
  handleAddNewItem,
  handleDeleteItem,
  setCurrentDimensionUnit,
  setCurrentWeightUnit,
  isDisabledAddPackage = false,
}: UsePackageInfoParams) {
  const intl = useIntl()
  const {statesGlobal, getPackages} = useStatesGlobal()
  const orderHelper = OrderHelper()
  const packageTypePlaceholder = intl.formatMessage({id: 'PACKAGE_TYPE'})

  const [labelPackageType, setLabelPackageType] = useState<string | undefined>(
    packageTypePlaceholder
  )
  const [isDisabledPackageCount, setIsDisabledPackageCount] = useState(false)
  const [oneRateUnmatched, setOneRateUnmatched] = useState(false)

  const carriersPackage = statesGlobal?.carriersPackage
  const packages = statesGlobal.packages

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------

  const parcelsCount = Object.keys(formik.values.parcels || {}).length

  const reachLimitRow = useMemo(
    () => parcelsCount >= UNIT_VALUES.PACKAGE_COUNT.limit_row,
    [parcelsCount]
  )

  // ---------------------------------------------------------------------------
  // One-rate helpers
  // ---------------------------------------------------------------------------

  const oneRateItems = useMemo(() => {
    const group = carriersPackage?.find((g: any) => g.label === 'FedEx One Rate Package')
    return group?.value || []
  }, [carriersPackage])

  const isOneRatePackage = useMemo(() => {
    const parcels = formik.values.parcels || {}
    const firstKey = Object.keys(parcels)?.[0]
    const first = parcels?.[firstKey]
    if (!first?.package_type) return false
    return oneRateItems.some((item: any) => item.value === first.package_type)
  }, [formik.values.parcels, oneRateItems])

  const findBestFitOneRate = useCallback(
    (userL: number, userW: number, userH: number, dimUnit: string) => {
      const toInches = (val: number, unit: string) => {
        if (unit === DIMENSION_UNIT.IN) return val
        return convertUnit({value: val, unit}, {unit: DIMENSION_UNIT.IN})
      }

      const userDims = [
        toInches(userL, dimUnit),
        toInches(userW, dimUnit),
        toInches(userH, dimUnit),
      ].sort((a, b) => a - b)

      // All dimensions must be positive to match a package
      if (userDims[0] <= 0) return null

      let bestFit: any = null
      let bestVolume = Infinity

      for (const item of oneRateItems) {
        const boxDims = [
          Number(item.parcel?.length || 0),
          Number(item.parcel?.width || 0),
          Number(item.parcel?.height || 0),
        ].sort((a, b) => a - b)

        if (userDims[0] <= boxDims[0] && userDims[1] <= boxDims[1] && userDims[2] <= boxDims[2]) {
          const volume = boxDims[0] * boxDims[1] * boxDims[2]
          if (volume < bestVolume) {
            bestVolume = volume
            bestFit = item
          }
        }
      }

      return bestFit
    },
    [oneRateItems]
  )

  // ---------------------------------------------------------------------------
  // Disabled-state logic
  // ---------------------------------------------------------------------------

  const isDisabledDimension = useMemo(() => {
    if (disabled) return true
    if (oneRateUnmatched) return false
    if (parcelsCount > 1) return false
    if (labelPackageType === packageTypePlaceholder) return false

    const parcels = formik.values.parcels || {}
    const firstKey = Object.keys(parcels)?.[0]
    if (parcels?.[firstKey]?.package_id) return false

    return !(
      labelPackageType === OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label ||
      labelPackageType === OPTION_PACKAGE_TYPE.CUSTOM_PACKAGE.label ||
      labelPackageType?.toLowerCase().includes('cubic') ||
      isOneRatePackage
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    disabled,
    labelPackageType,
    parcelsCount,
    isOneRatePackage,
    oneRateUnmatched,
    packageTypePlaceholder,
  ])

  useEffect(() => {
    // Only auto-reset oneRateUnmatched when the user explicitly selects a non-one-rate package.
    // When oneRateUnmatched is true, package_type is cleared which makes isOneRatePackage false,
    // but we need the banner to persist until the user makes a new selection.
    if (!isOneRatePackage && !oneRateUnmatched) setOneRateUnmatched(false)
  }, [isOneRatePackage, oneRateUnmatched])

  useEffect(() => {
    const parcels = formik.values.parcels || {}
    const firstKey = Object.keys(parcels)?.[0]
    const first = parcels?.[firstKey]

    const flag =
      isDisabledDimension ||
      ((labelPackageType?.toLowerCase().includes('cubic') && first?.package_type) ?? false)

    setIsDisabledPackageCount(flag)
  }, [formik.values.parcels, labelPackageType, isDisabledDimension])

  // ---------------------------------------------------------------------------
  // Unit initialisation
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const parcels = formik.values.parcels
    const firstParcel = Object.values(parcels)?.[0] as ParcelItem
    if (!firstParcel) return

    const weightUnit = firstParcel.weight_unit || WEIGHT_UNIT.OZ
    const dimensionUnit = firstParcel.dimension_unit || DIMENSION_UNIT.IN

    setCurrentWeightUnit?.(weightUnit)
    setCurrentDimensionUnit?.(dimensionUnit)

    formik.setFieldValue('weight_unit', weightUnit)
    formik.setFieldValue('dimension_unit', dimensionUnit)

    if (!firstParcel.weight_unit || !firstParcel.dimension_unit) {
      Object.keys(parcels).forEach((key) => {
        formik.setFieldValue(`parcels.${key}.weight_unit`, weightUnit)
        formik.setFieldValue(`parcels.${key}.dimension_unit`, dimensionUnit)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.parcels])

  // ---------------------------------------------------------------------------
  // Totals & validation side-effects
  // ---------------------------------------------------------------------------

  const totalWeightPackage = useMemo(() => {
    let sum = 0
    Object.values(formik.values.parcels).forEach((value: any) => {
      sum +=
        (Number.parseFloat(value?.weight) || 0) * (Number.parseFloat(value?.package_count) || 1)
    })
    return round(sum)
  }, [formik.values.parcels])

  const totalWeightCustoms = useMemo(() => {
    let sum = 0
    Object.values(formik?.values?.custom_items || {}).forEach((value: any) => {
      sum += Number.parseFloat(value?.weight) || 0
    })
    return round(sum)
  }, [formik.values.custom_items])

  useEffect(() => {
    if (!formik.values.custom_items || !Object.keys(formik.values.parcels).length) return
    const weight_unit = get(Object.values(formik.values.parcels), '[0].weight_unit', '')
    if (!weight_unit) return

    const converted = convertUnit(
      {unit: WEIGHT_UNIT.OZ, value: totalWeightCustoms},
      {unit: weight_unit}
    )
    formik.setFieldValue('check_weight', converted > totalWeightPackage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.parcels, formik.values.custom_items, totalWeightCustoms, totalWeightPackage])

  const totalPackageCount = useMemo(() => {
    let sum = 0
    Object.values(formik.values.parcels).forEach((value: any) => {
      sum += Number.parseFloat(value?.package_count) || 1
    })
    return sum
  }, [formik.values.parcels])

  useEffect(() => {
    const isFedex = FEDEX_INTERNATIONAL_SERVICES.includes(formik.values.service)
    const maxAllowed = isFedex ? FEDEX_PARCEL_LIMIT : UNIT_VALUES.PACKAGE_COUNT.max

    let hasRowExceedingLimit = false
    if (isFedex) {
      Object.values(formik.values.parcels).forEach((value: any) => {
        const packageCountItem = Number.parseFloat(value?.package_count) || 1
        if (packageCountItem > FEDEX_PARCEL_LIMIT) {
          hasRowExceedingLimit = true
        }
      })
    }

    formik.setFieldValue(
      'check_package_count',
      totalPackageCount > maxAllowed || hasRowExceedingLimit
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPackageCount, formik.values.service])

  const totalPackages = useMemo(() => {
    let sum = 0
    Object.values(formik.values.parcels).forEach((value: any) => {
      sum += Number.parseFloat(value?.package_count) || 0
    })
    return sum
  }, [formik.values.parcels])

  // ---------------------------------------------------------------------------
  // Package-type options
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (hasGetPackage) getPackages()
  }, [getPackages, hasGetPackage])

  const optionsCustomPackage = useMemo(() => {
    if (!packages) return [OPTION_PACKAGE_TYPE.CUSTOM_PACKAGE]

    return {
      label: OPTION_PACKAGE_TYPE.CUSTOM_PACKAGE.label,
      options: packages.map((item: any) => ({
        label: item.name,
        value: {
          length: item.length,
          width: item.width,
          height: item.height,
          dimension_unit: item.dimension_unit,
          package_id: item.id,
          package_type: '',
          package_count: item?.package_count || 1,
        },
      })),
    }
  }, [packages])

  const optionsPackageTypes = useMemo(() => {
    if (isEmpty(carriersPackage)) return [OPTION_PACKAGE_TYPE.CARRIER_STANDARD_PACKAGE]

    return carriersPackage.map((parent: any) => ({
      label: parent.label,
      options: parent?.value.map((item: any) => ({
        label: item.label,
        value: {
          length: item.parcel.length,
          width: item.parcel.width,
          height: item.parcel.height,
          dimension_unit: item.parcel.dimension_unit,
          weight_limit: item.parcel.weight_limit,
          package_type: item.value,
          package_id: null,
          package_count: item?.package_count || 1,
        },
      })),
    }))
  }, [carriersPackage])

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const addPackage = useCallback(() => {
    handleAddNewItem?.('parcels')
  }, [handleAddNewItem])

  const removePackage = useCallback(
    (key: string) => {
      handleDeleteItem?.('parcels', key)
    },
    [handleDeleteItem]
  )

  const onChangeDimensionUnit = useCallback(
    (e: {value: string}) => {
      setCurrentDimensionUnit?.(e.value)

      const parcels = formik.values.parcels
      const firstParcelKey = Object.keys(parcels)?.[0]
      Object.keys(parcels).forEach((key) => {
        formik.setFieldValue(`parcels.${key}.dimension_unit`, e.value)
      })

      const firstParcel = parcels[firstParcelKey]
      const {package_type} = firstParcel || {}
      const {editable, fieldsHidden, packageType} = orderHelper.getPackageDetails(package_type)

      if (editable && fieldsHidden && packageType) {
        ;['length', 'width', 'height'].forEach((field) => {
          if (fieldsHidden.includes(field)) {
            const baseValue = packageType?.parcel?.[field]
            const updatedValue = convertUnit(
              {unit: DIMENSION_UNIT.IN, value: baseValue},
              {unit: e.value}
            ).toString()
            formik.setFieldValue(`parcels.${firstParcelKey}.${field}`, updatedValue)
          }
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.values.parcels, setCurrentDimensionUnit, orderHelper]
  )

  const onChangeWeightUnit = useCallback(
    (e: {value: string}) => {
      setCurrentWeightUnit?.(e.value)
      const parcels = formik.values.parcels
      Object.keys(parcels).forEach((key) => {
        formik.setFieldValue(`parcels.${key}.weight_unit`, e.value)
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.values.parcels, setCurrentWeightUnit]
  )

  const onDimensionChange = useCallback(
    (keyItem: string, dimKey: string, event: any) => {
      if (labelPackageType?.toLowerCase().includes('cubic')) return

      if (isOneRatePackage || oneRateUnmatched) {
        const parcel = formik.values.parcels?.[keyItem]
        const dimUnit = parcel?.dimension_unit || DIMENSION_UNIT.IN
        const newVal = Number(event?.target?.value) || 0
        const dims = {
          length: Number(parcel?.length) || 0,
          width: Number(parcel?.width) || 0,
          height: Number(parcel?.height) || 0,
        }
        dims[dimKey] = newVal

        const bestFit = findBestFitOneRate(dims.length, dims.width, dims.height, dimUnit)
        if (bestFit) {
          formik.setFieldValue(`parcels.${keyItem}.package_type`, bestFit.value)
          setOneRateUnmatched(false)
        } else {
          formik.setFieldValue(`parcels.${keyItem}.package_type`, '')
          formik.setFieldValue(`parcels.${keyItem}.package_id`, null)
          setOneRateUnmatched(true)
        }
        return
      }

      formik.setFieldValue(`parcels.${keyItem}.package_type`, null)
      formik.setFieldValue(`parcels.${keyItem}.package_id`, null)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      labelPackageType,
      isOneRatePackage,
      oneRateUnmatched,
      formik.values.parcels,
      findBestFitOneRate,
    ]
  )

  // ---------------------------------------------------------------------------
  // Select styling helper
  // ---------------------------------------------------------------------------

  const customSelectInputStyle = useCallback((isDisabled?: boolean) => {
    const color = isDisabled ? '#a1a5b7' : '#0B6D65'
    return {
      styleInput: {
        background: 'transparent',
        width: '80px',
        backgroundImage: 'none',
        borderColor: 'transparent',
        '&:hover': {borderColor: 'transparent', background: '#F0FDFA'},
        color,
        textAlign: 'right',
      },
      styleDropdownIndicator: {
        color,
        '&:hover': {color},
        padding: 0,
        marginRight: 0,
      },
      customMenuPortal: {marginTop: '-14px'},
    }
  }, [])

  return {
    // state
    labelPackageType,
    setLabelPackageType,
    oneRateUnmatched,
    isDisabledPackageCount,
    isDisabledDimension,
    isDisabledAddPackage,
    reachLimitRow,
    parcelsCount,
    carriersPackage,

    // data
    packageTypePlaceholder,
    optionsPackageTypes,
    optionsCustomPackage,
    totalWeightPackage,
    totalPackages,
    isOneRatePackage,
    setOneRateUnmatched,

    // handlers
    addPackage,
    removePackage,
    onChangeDimensionUnit,
    onChangeWeightUnit,
    onDimensionChange,
    customSelectInputStyle,
  }
}
